export const FLOW_ID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;

export const IDENTITY_PROXY_PATH = "/identity";
export const SESSION_AALS = new Set(["aal1", "aal2"]);

export class IdentityBrowserContractError extends Error {
  constructor(message, { kind = "invalid" } = {}) {
    super(message);
    this.name = "IdentityBrowserContractError";
    this.kind = kind;
  }
}

export function invalid(message, kind = "invalid") {
  throw new IdentityBrowserContractError(message, { kind });
}

export function isRecord(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

export function requiredString(value, label, { maxLength = 4096 } = {}) {
  if (typeof value !== "string" || value.length === 0 || value.length > maxLength) {
    invalid(`${label} is invalid`);
  }
  return value;
}

export function optionalString(value, label, { maxLength = 4096 } = {}) {
  if (value === undefined || value === null) {
    return "";
  }
  if (typeof value !== "string" || value.length > maxLength) {
    invalid(`${label} is invalid`);
  }
  return value;
}

export function optionalBoolean(value, label) {
  if (value === undefined) {
    return false;
  }
  if (typeof value !== "boolean") {
    invalid(`${label} is invalid`);
  }
  return value;
}

export function exactQuery(url, expectedName, expectedValue, label) {
  const entries = [...url.searchParams.entries()];
  if (
    entries.length !== 1
    || entries[0][0] !== expectedName
    || entries[0][1] !== expectedValue
  ) {
    invalid(`${label} query is invalid`);
  }
}

export function optionalPositiveInteger(value, label, { max = 4096 } = {}) {
  if (value === undefined) {
    return 0;
  }
  if (!Number.isInteger(value) || value <= 0 || value > max) {
    invalid(`${label} is invalid`);
  }
  return value;
}

export function requiredPositiveInteger(value, label, { max = Number.MAX_SAFE_INTEGER } = {}) {
  if (!Number.isInteger(value) || value <= 0 || value > max) {
    invalid(`${label} is invalid`);
  }
  return value;
}

export function boundedMessage(message, label) {
  if (!isRecord(message)) {
    invalid(`${label} is invalid`);
  }
  const type = requiredString(message.type, `${label} type`, { maxLength: 16 });
  if (!["error", "info", "success"].includes(type)) {
    invalid(`${label} type is unsupported`, "unsupported");
  }
  return Object.freeze({
    type,
    text: requiredString(message.text, `${label} text`, { maxLength: 1000 }),
  });
}

export function boundedMessages(value, label) {
  if (value === undefined || value === null) {
    return Object.freeze([]);
  }
  if (!Array.isArray(value) || value.length > 16) {
    invalid(`${label} is invalid`);
  }
  return Object.freeze(value.map((message, index) => (
    boundedMessage(message, `${label} ${index}`)
  )));
}

export function boundedNodeLabel(meta, label) {
  if (meta === undefined || meta === null) {
    return "";
  }
  if (!isRecord(meta) || (meta.label !== undefined && !isRecord(meta.label))) {
    invalid(`${label} metadata is invalid`);
  }
  if (meta.label === undefined) {
    return "";
  }
  return requiredString(meta.label.text, `${label} label`, { maxLength: 200 });
}

export function validateAttributeKeys(attributes, allowedKeys, label) {
  for (const key of Object.keys(attributes)) {
    if (!allowedKeys.has(key)) {
      invalid(`${label} attribute ${key} is unsupported`, "unsupported");
    }
  }
}

export function validateExactKeys(value, allowedKeys, label) {
  if (!isRecord(value)) {
    invalid(`${label} is invalid`);
  }
  for (const key of Object.keys(value)) {
    if (!allowedKeys.has(key)) {
      invalid(`${label} field ${key} is unsupported`, "unsupported");
    }
  }
}

export function boundedTimestamp(value, label) {
  const timestamp = requiredString(value, label, { maxLength: 64 });
  if (
    !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,9})?(?:Z|[+-]\d{2}:\d{2})$/.test(timestamp)
    || !Number.isFinite(Date.parse(timestamp))
  ) {
    invalid(`${label} is invalid`);
  }
  return timestamp;
}
